import type { TranslationDict } from '../types';

export const settings: TranslationDict = {
  title: {
    en: 'Settings',
    'zh-CN': '设置',
    ja: '設定',
  },
  back: {
    en: 'Back',
    'zh-CN': '返回',
    ja: '戻る',
  },
  plan: {
    title: {
      en: 'Your Plan',
      'zh-CN': '你的套餐',
      ja: '現在のプラン',
    },
    upgrade: {
      en: 'Upgrade ->',
      'zh-CN': '升级 ->',
      ja: 'アップグレード ->',
    },
    manage: {
      en: 'Manage ->',
      'zh-CN': '管理 ->',
      ja: '管理 ->',
    },
    expires: {
      en: 'Expires {{date}}',
      'zh-CN': '到期时间 {{date}}',
      ja: '有効期限 {{date}}',
    },
  },
  language: {
    title: {
      en: 'Language',
      'zh-CN': '语言',
      ja: '言語',
    },
  },
  appearance: {
    title: {
      en: 'Appearance',
      'zh-CN': '外观',
      ja: '表示',
    },
    darkMode: {
      en: 'Dark Mode',
      'zh-CN': '深色模式',
      ja: 'ダークモード',
    },
    themeColor: {
      en: 'Theme Color',
      'zh-CN': '主题色',
      ja: 'テーマカラー',
    },
  },
  legal: {
    title: {
      en: 'Legal',
      'zh-CN': '法律信息',
      ja: '法的情報',
    },
  },
  dataExport: {
    title: {
      en: 'Data Export',
      'zh-CN': '数据导出',
      ja: 'データエクスポート',
    },
    description: {
      en: 'Download your profile data as JSON.',
      'zh-CN': '将你的资料数据导出为 JSON。',
      ja: 'プロフィールデータを JSON でダウンロードします。',
    },
    action: {
      en: 'Export Data',
      'zh-CN': '导出数据',
      ja: 'データをエクスポート',
    },
  },
  blockedAccounts: {
    title: {
      en: 'Blocked Accounts',
      'zh-CN': '已屏蔽的账号',
      ja: 'ブロック中のアカウント',
    },
    empty: {
      en: 'No blocked accounts',
      'zh-CN': '没有已屏蔽的账号',
      ja: 'ブロック中のアカウントはありません',
    },
    count: {
      en: '{{count}} blocked account(s)',
      'zh-CN': '{{count}} 个已屏蔽的账号',
      ja: '{{count}} 件のブロック中アカウント',
    },
    unblock: {
      en: 'Unblock',
      'zh-CN': '取消屏蔽',
      ja: 'ブロック解除',
    },
    unblockSuccess: {
      en: 'User unblocked',
      'zh-CN': '已取消屏蔽',
      ja: 'ブロックを解除しました',
    },
    unblockError: {
      en: 'Failed to unblock user',
      'zh-CN': '取消屏蔽失败',
      ja: 'ブロック解除に失敗しました',
    },
  },
  dangerZone: {
    title: {
      en: 'Danger Zone',
      'zh-CN': '危险区域',
      ja: '危険ゾーン',
    },
    description: {
      en: 'Permanently delete your account. Your profile will be anonymized immediately. Posts and data will be permanently deleted after 30 days.',
      'zh-CN': '永久删除你的账号。你的个人资料将立即匿名化。帖子和数据将在 30 天后永久删除。',
      ja: 'アカウントを完全に削除します。プロフィールは即座に匿名化されます。投稿とデータは30日後に完全に削除されます。',
    },
    deleteButton: {
      en: 'Delete Account',
      'zh-CN': '删除账号',
      ja: 'アカウントを削除',
    },
  },
  deleteAccount: {
    title: {
      en: 'Delete Account',
      'zh-CN': '删除账号',
      ja: 'アカウントを削除',
    },
    warning: {
      en: 'This action cannot be undone. Your profile will be anonymized immediately and all your data will be permanently deleted after 30 days.',
      'zh-CN': '此操作不可撤销。你的个人资料将立即匿名化，所有数据将在 30 天后永久删除。',
      ja: 'この操作は取り消せません。プロフィールは即座に匿名化され、すべてのデータは30日後に完全に削除されます。',
    },
    bullet1: {
      en: 'Your profile will be anonymized to "Deleted User"',
      'zh-CN': '你的个人资料将被匿名化为「已删除用户」',
      ja: 'プロフィールは「削除済みユーザー」に匿名化されます',
    },
    bullet2: {
      en: 'Your username will be freed for others to register',
      'zh-CN': '你的用户名将被释放，其他人可以注册',
      ja: 'ユーザー名は解放され、他の人が登録できるようになります',
    },
    bullet3: {
      en: 'All posts, comments, and data will be permanently deleted after 30 days',
      'zh-CN': '所有帖子、评论和数据将在 30 天后永久删除',
      ja: 'すべての投稿、コメント、データは30日後に完全に削除されます',
    },
    typeConfirm: {
      en: 'Type "{{text}}" to confirm',
      'zh-CN': '输入「{{text}}」以确认',
      ja: '「{{text}}」と入力して確認',
    },
    passwordLabel: {
      en: 'Enter your password',
      'zh-CN': '输入你的密码',
      ja: 'パスワードを入力',
    },
    passwordPlaceholder: {
      en: 'Your current password',
      'zh-CN': '你的当前密码',
      ja: '現在のパスワード',
    },
    submit: {
      en: 'Delete My Account',
      'zh-CN': '删除我的账号',
      ja: 'アカウントを削除する',
    },
    verifying: {
      en: 'Verifying...',
      'zh-CN': '验证中...',
      ja: '確認中...',
    },
    deleting: {
      en: 'Deleting...',
      'zh-CN': '删除中...',
      ja: '削除中...',
    },
    success: {
      en: 'Account deleted',
      'zh-CN': '账号已删除',
      ja: 'アカウントが削除されました',
    },
    redirecting: {
      en: 'Redirecting to homepage...',
      'zh-CN': '正在跳转到首页...',
      ja: 'ホームページにリダイレクト中...',
    },
    invalidPassword: {
      en: 'Incorrect password. Please try again.',
      'zh-CN': '密码错误，请重试。',
      ja: 'パスワードが間違っています。もう一度お試しください。',
    },
    error: {
      en: 'Failed to delete account. Please try again.',
      'zh-CN': '删除账号失败，请重试。',
      ja: 'アカウントの削除に失敗しました。もう一度お試しください。',
    },
  },
  about: {
    footer: {
      en: 'Kommend v1.0 · © {{year}} Kommend. All rights reserved.',
      'zh-CN': 'Kommend v1.0 · © {{year}} Kommend。保留所有权利。',
      ja: 'Kommend v1.0 · © {{year}} Kommend. All rights reserved.',
    },
  },
};

